import {
  TextareaHTMLAttributes,
  forwardRef,
  useCallback,
  useLayoutEffect,
  useRef,
} from "react";
import { getCompactInputClasses } from "./inputStyles";

export interface CompactTextAreaInputProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Show error styling */
  hasError?: boolean;
  /** Additional CSS classes on the textarea element */
  inputClassName?: string;
  /** Enable auto-resize to fit content (default: true) */
  autoResize?: boolean;
}

/** Auto-resize a textarea to its scroll height. */
const resizeTextArea = (el: HTMLTextAreaElement) => {
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
};

/**
 * Compact textarea that shares the same border, focus, and colour
 * styles as CompactTextInput but renders a `<textarea>`.
 *
 * By default it auto-resizes to fit content on change and focus.
 * Use for table cells, inline forms, and other tight layouts.
 */
export const CompactTextAreaInput = forwardRef<
  HTMLTextAreaElement,
  CompactTextAreaInputProps
>(
  (
    {
      hasError = false,
      inputClassName = "",
      className = "",
      autoResize = true,
      rows = 1,
      value,
      onChange,
      onFocus,
      ...props
    },
    ref
  ) => {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);

    const setRefs = useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    // Resize on mount and whenever the value changes externally (e.g. when a
    // row enters edit mode pre-filled with a multi-line description), not just
    // on user input/focus.
    useLayoutEffect(() => {
      if (autoResize && innerRef.current) {
        resizeTextArea(innerRef.current);
      }
    }, [value, autoResize]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (autoResize) {
          resizeTextArea(e.target);
        }
        onChange?.(e);
      },
      [autoResize, onChange]
    );

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLTextAreaElement>) => {
        if (autoResize) {
          resizeTextArea(e.target);
        }
        onFocus?.(e);
      },
      [autoResize, onFocus]
    );

    return (
      <textarea
        ref={setRefs}
        rows={rows}
        value={value}
        className={getCompactInputClasses(
          hasError,
          `text-sm resize-none overflow-hidden ${inputClassName} ${className}`
        )}
        onChange={handleChange}
        onFocus={handleFocus}
        {...props}
      />
    );
  }
);

CompactTextAreaInput.displayName = "CompactTextAreaInput";
